import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import Nav from "@/components/Nav";
import PrepareNextCall from "@/components/PrepareNextCall";
import WorkingHourglass from "@/components/WorkingHourglass";
import {
  ArrowClockwise,
  ArrowRight,
  Buildings,
  MagnifyingGlass,
  Phone,
  Plus,
  Target,
  Users,
  X,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function PrepareCustomerRow({ customer, loading, onSelect }) {
  const inProgress = Boolean(customer?.prepare_next_call_generation?.in_progress);
  return (
    <button
      type="button"
      className="prepare-next-call-selector__row"
      onClick={() => onSelect(customer)}
      disabled={loading}
      aria-label={inProgress
        ? `Preparation in progress for ${customer.name}`
        : `Prepare next call for ${customer.name}`}
    >
      <span className="prepare-next-call-selector__mark" aria-hidden="true">
        <Target size={21} weight="duotone" />
      </span>

      <span className="prepare-next-call-selector__identity">
        <strong>{customer.name}</strong>
        <span>
          {customer.company ? (
            <span>
              <Buildings size={13} />
              {customer.company}
            </span>
          ) : null}
          <span>
            <Phone size={13} />
            {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
          </span>
        </span>
      </span>

      <span className="prepare-next-call-selector__latest">
        <small>Latest call</small>
        {formatDate(customer.last_call_at) || "—"}
      </span>

      <span
        className={`prepare-next-call-selector__action${inProgress ? " is-progress" : ""}`}
        aria-hidden="true"
      >
        {loading ? (
          <WorkingHourglass size={16} />
        ) : inProgress ? (
          <>
            <WorkingHourglass size={15} />
            <span>In progress</span>
          </>
        ) : (
          <ArrowRight size={15} weight="bold" />
        )}
      </span>
    </button>
  );
}

export default function PrepareNextCallPage() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const [loadingKey, setLoadingKey] = useState("");
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [memoryRefreshing, setMemoryRefreshing] = useState(false);

  const loadCustomers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    try {
      const { data } = await api.get("/customers");
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      if (!silent) {
        setLoadError(getActionableError(error, "Callsect could not load your customers."));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const hasPreparationInProgress = customers.some(
    (customer) => customer?.prepare_next_call_generation?.in_progress
  );

  useEffect(() => {
    if (!hasPreparationInProgress) return undefined;
    const intervalId = window.setInterval(() => {
      void loadCustomers({ silent: true });
    }, 2500);
    return () => window.clearInterval(intervalId);
  }, [hasPreparationInProgress, loadCustomers]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredCustomers = useMemo(() => customers.filter((customer) => (
    !normalizedQuery
    || (customer.name || "").toLowerCase().includes(normalizedQuery)
    || (customer.company || "").toLowerCase().includes(normalizedQuery)
  )), [customers, normalizedQuery]);

  const selectCustomer = async (customer) => {
    if (!customer?.key || loadingKey) return;
    setLoadingKey(customer.key);
    setSelectionError("");
    setSelectedTimeline(null);
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(customer.key)}/timeline`);
      if (!data?.relationship_memory?.available || data?.relationship_memory?.needs_rebuild) {
        setSelectedTimeline(data);
        return;
      }
      setSelectedTimeline(data);
    } catch (error) {
      setSelectionError(getActionableError(error, "Callsect could not open this customer relationship."));
    } finally {
      setLoadingKey("");
    }
  };

  const refreshSelectedMemory = async () => {
    const relationshipId = selectedTimeline?.customer?.id || selectedTimeline?.customer?.key;
    if (!relationshipId) return;

    setMemoryRefreshing(true);
    try {
      const result = await api.post(`/customers/${encodeURIComponent(relationshipId)}/memory/rebuild`);
      const refreshed = await api.get(`/customers/${encodeURIComponent(relationshipId)}/timeline`);
      setSelectedTimeline(refreshed.data);
      toast.success(`Relationship memory built from ${result.data.processed_calls} calls`);
    } catch (error) {
      const message = getActionableError(error, "Relationship memory could not be rebuilt.");
      toast.error(message);
      throw error;
    } finally {
      setMemoryRefreshing(false);
    }
  };

  const selectedCustomer = selectedTimeline?.customer;
  const selectedMilestones = selectedTimeline?.milestones || [];
  const selectedRelationshipId = selectedCustomer?.id || selectedCustomer?.key || "";
  const selectedPreparationInProgress = Boolean(
    selectedTimeline?.relationship_memory?.prepare_next_call_generation?.in_progress
  );

  useEffect(() => {
    if (!selectedRelationshipId || !selectedPreparationInProgress) return undefined;
    let refreshRunning = false;
    let disposed = false;
    const refresh = async () => {
      if (refreshRunning) return;
      refreshRunning = true;
      try {
        const { data } = await api.get(
          `/customers/${encodeURIComponent(selectedRelationshipId)}/timeline`
        );
        if (!disposed) setSelectedTimeline(data);
        void loadCustomers({ silent: true });
      } catch {
        // Keep the last truthful in-progress state during transient refresh failures.
      } finally {
        refreshRunning = false;
      }
    };
    const intervalId = window.setInterval(refresh, 2000);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [selectedRelationshipId, selectedPreparationInProgress, loadCustomers]);

  const handlePreparationStateChange = useCallback((state) => {
    const inProgress = Boolean(state?.in_progress);
    const mode = state?.mode || "zero_input";
    const startedAt = state?.started_at || "";
    setSelectedTimeline((current) => current ? {
      ...current,
      relationship_memory: {
        ...(current.relationship_memory || {}),
        prepare_next_call_generation: inProgress
          ? { in_progress: true, mode, started_at: startedAt }
          : { in_progress: false },
      },
    } : current);
    setCustomers((current) => current.map((item) => (
      item.key === selectedRelationshipId
        ? {
            ...item,
            prepare_next_call_generation: inProgress
              ? { in_progress: true, mode, started_at: startedAt }
              : { in_progress: false },
          }
        : item
    )));
  }, [selectedRelationshipId]);

  const handlePreparationClose = useCallback((state) => {
    if (state?.in_progress) {
      handlePreparationStateChange(state);
    }
    setSelectedTimeline(null);
    void loadCustomers({ silent: true });
  }, [handlePreparationStateChange, loadCustomers]);

  return (
    <div className="customers-page prepare-next-call-launcher-page">
      <Nav />

      <main className="customers-shell prepare-next-call-launcher-shell product-page-enter">
        <header className="customers-hero prepare-next-call-launcher-hero">
          <div className="customers-hero__copy">
            <div className="brand-eyebrow">PREPARE NEXT CALL</div>
            <h1>Prepare for your next conversation</h1>
            <p>Choose a customer to reopen the relationship context and build your next-call plan.</p>
          </div>
        </header>

        <section className="customers-directory prepare-next-call-selector" aria-label="Choose a customer">
          <div className="customers-directory__toolbar prepare-next-call-selector__toolbar">
            {!loading && customers.length > 0 ? (
              <label className="customers-search">
                <MagnifyingGlass size={16} aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search customers or companies..."
                  aria-label="Search customers or companies"
                />
                {query ? (
                  <button type="button" onClick={() => setQuery("")} aria-label="Clear customer search">
                    <X size={14} weight="bold" />
                  </button>
                ) : null}
                <span className="customers-search__count">
                  {filteredCustomers.length}/{customers.length}
                </span>
              </label>
            ) : null}
          </div>

          {selectionError ? (
            <div className="prepare-next-call-selector__notice" role="status">
              <Target size={18} weight="duotone" />
              <span>{selectionError}</span>
            </div>
          ) : null}

          {loading ? (
            <div className="customers-loading" aria-label="Loading customers">
              {[0, 1, 2, 3].map((item) => (
                <div className="customers-skeleton" key={item}>
                  <div className="customers-skeleton__mark" />
                  <div className="customers-skeleton__copy">
                    <i />
                    <i />
                  </div>
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="customers-empty customers-empty--error" role="alert">
              <div className="customers-empty__icon"><ArrowClockwise size={25} weight="duotone" /></div>
              <strong>Your customers could not be loaded</strong>
              <p>{loadError}</p>
              <button type="button" className="customers-secondary-button" onClick={loadCustomers}>
                <ArrowClockwise size={15} weight="bold" />
                Try again
              </button>
            </div>
          ) : customers.length === 0 ? (
            <div className="customers-empty customers-empty--activation">
              <div className="customers-empty__icon"><Users size={26} weight="duotone" /></div>
              <strong>Add a call before preparing the next one.</strong>
              <p>Prepare Next Call uses the relationship context Callsect builds from your customer conversations.</p>
              <Link to="/upload" className="customers-primary-button customers-primary-button--compact">
                <Plus size={15} weight="bold" />
                Add a new call
              </Link>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="customers-empty customers-empty--compact">
              <div className="customers-empty__icon"><MagnifyingGlass size={24} /></div>
              <strong>No matching customers</strong>
              <p>Try a different customer or company name.</p>
              <button type="button" className="customers-secondary-button" onClick={() => setQuery("")}>Clear search</button>
            </div>
          ) : (
            <div className="prepare-next-call-selector__list">
              {filteredCustomers.map((customer) => (
                <PrepareCustomerRow
                  key={customer.key}
                  customer={customer}
                  loading={loadingKey === customer.key}
                  onSelect={selectCustomer}
                />
              ))}
            </div>
          )}

        </section>

        {selectedTimeline && selectedCustomer && (
          !selectedTimeline.relationship_memory?.available
          || selectedTimeline.relationship_memory?.needs_rebuild
        ) ? (
          <section className="customers-empty customers-empty--activation" role="status">
            <div className="customers-empty__icon"><ArrowClockwise size={25} weight="duotone" /></div>
            <strong>
              {selectedTimeline.relationship_memory?.needs_rebuild
                ? "Relationship Memory needs to be repaired"
                : "Relationship Memory needs to be built"}
            </strong>
            <p>
              This reprocesses the stored calls and repairs the relationship context. It does not simply generate another version of the current preparation.
            </p>
            <button
              type="button"
              className="customers-primary-button customers-primary-button--compact"
              onClick={refreshSelectedMemory}
              disabled={memoryRefreshing}
            >
              {memoryRefreshing ? <WorkingHourglass size={15} /> : <ArrowClockwise size={15} weight="bold" />}
              {memoryRefreshing ? "Repairing memory..." : "Repair Relationship Memory"}
            </button>
          </section>
        ) : null}

        {selectedTimeline?.relationship_memory?.available
          && !selectedTimeline.relationship_memory?.needs_rebuild
          && selectedCustomer ? (
          <PrepareNextCall
            key={selectedCustomer.id || selectedCustomer.key}
            memory={selectedTimeline.relationship_memory}
            customer={{
              id: selectedCustomer.id || selectedCustomer.key,
              name: selectedCustomer.name,
              call_count: selectedMilestones.length,
            }}
            milestones={[...selectedMilestones].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )}
            openOnMount
            showPreview={false}
            onGenerationStateChange={handlePreparationStateChange}
            onClose={handlePreparationClose}
          />
        ) : null}
      </main>
    </div>
  );
}
